import { orgSelectors } from 'ducks/orgs'
import { BulkOptionType } from 'elements/hardwareSelectorV2/types'
import { getSupplierConfigNonReact } from 'pages/ordering/hooks/useGetSupplierConfig'
import { Deal } from 'pages/ordering/deals/types'
import type LineItemType from 'pages/ordering/OrderLineItem'
import OrderLineItem from 'pages/ordering/OrderLineItem'
import ProjectOrderPresenter from 'pages/ordering/ProjectOrderPresenter/projectOrderPresenter'
import type { HardwareSupplierFilterKeyType, LineItemLoadedDataType, ProjectOrderDetail } from 'pages/ordering/type'
import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import appStorage from 'storage/appStorage'
import { ReduxActionType } from 'types/global'
import { RootState } from 'types/state'
import { createCachedReduxSelector } from 'util/createCachedReduxSelector'
import { authSelectors, SET_ORG_ID } from './auth'

const OPEN_ORDER_CART = 'OPEN_ORDER_CART'
const UPDATE_LOADED_PRODUCT_DATA = 'UPDATE_LOADED_PRODUCT_DATA'
const ADD_LINE_ITEMS = 'ADD_LINE_ITEMS'
const DELETE_LINE_ITEMS = 'DELETE_LINE_ITEMS'
const UPDATE_LINE_ITEM = 'UPDATE_LINE_ITEM'
const CLEAR_LINE_ITEMS = 'CLEAR_LINE_ITEMS'
const CONFIRM_LINE_ITEMS = 'CONFIRM_LINE_ITEMS'
const DELETE_LINE_ITEMS_BY_SYSTEM_ID = 'DELETE_LINE_ITEMS_BY_SYSTEM_ID'
const UPDATE_SELECTED_HARDWARE_SUPPLIER = 'CHANGE_HARDWARE_SUPPLIER'
const UPDATE_IS_CUSTOM_PRICING = 'CHANGE_IS_CUSTOM_PRICING'
const UPDATE_LINE_ITEM_SUPPLIER = 'UPDATE_LINE_ITEM_SUPPLIER'
const UPDATE_LINE_ITEM_BY_CODE = 'UPDATE_LINE_ITEM_BY_CODE'
const SET_CART_DEALS = 'SET_CART_DEALS'
type LineItemIdentifiableKeys = 'uuid' | 'code' | 'orgId' | 'componentType' | 'status' | 'variantId' | 'confirmed'

export const SHOPPING_CART_STORAGE_KEY = 'shopping_cart_line_items'

export function persistCart(lineItems: OrderLineItem[]) {
  appStorage.setJSON(SHOPPING_CART_STORAGE_KEY, lineItems)
}

export function clearPersistedCart() {
  appStorage.setJSON(SHOPPING_CART_STORAGE_KEY, undefined)
}

export type OrderComponentsType = {
  openDialog: boolean
  lineItems: LineItemType[]
  selectedHardwareSupplier: HardwareSupplierFilterKeyType | 'component' | undefined
  usingCustomPricing: boolean
  /** undefined = not loaded; [] = loaded with no deals */
  cartDeals: Deal[] | undefined
}

const getInitialState = (): OrderComponentsType => {
  // TODO - there is some possibility that this is stored as an integer representing the old enum type.
  // We should convert that into the equivalent supplier filterKey value.
  const initialHardwareSupplier = appStorage.getString('selected_hardware_supplier')
  const lineItemsRaw = appStorage.getJSON<OrderLineItem[]>(SHOPPING_CART_STORAGE_KEY) as OrderLineItem[]

  let lineItems = lineItemsRaw
    ? lineItemsRaw.map((li) => {
        return new OrderLineItem({ ...li })
      })
    : []

  return {
    openDialog: false,
    lineItems: lineItems,
    selectedHardwareSupplier: initialHardwareSupplier || undefined,
    usingCustomPricing: false,
    cartDeals: undefined,
  }
}

export const setCartDeals = (deals: Deal[]) => ({
  type: SET_CART_DEALS,
  payload: deals,
})

type UpdateLineItemAction<T extends keyof LineItemType> = {
  type: typeof UPDATE_LINE_ITEM
  payload: {
    uuid: string
    source: T
    value: LineItemType[T]
  }
}

type UpdateLineItemActionByCode<T extends keyof LineItemType> = {
  type: typeof UPDATE_LINE_ITEM_BY_CODE
  payload: {
    code: string
    source: T
    value: LineItemType[T]
  }
}

export const openOrderCart = (open: boolean) => ({
  type: OPEN_ORDER_CART,
  payload: open,
})

export const addOrderLineItem = (lineItem: LineItemType) => {
  return addOrderLineItems([lineItem])
}

export const addOrderLineItems = (lineItems: LineItemType[]) => ({
  type: ADD_LINE_ITEMS,
  payload: {
    lineItems,
  },
})

export const clearLineItems = () => ({
  type: CLEAR_LINE_ITEMS,
  payload: {},
})

export const deleteUnconfirmedLineItems = () => {
  return deleteLineItems<'confirmed'>({ source: 'confirmed', identifiers: [false] })
}

export const deleteOrderByVariantId = (variantId: string) => {
  return deleteLineItems<'variantId'>({ source: 'variantId', identifiers: [variantId] })
}

export const deleteOrderByCode = (code: string) => {
  return deleteLineItems<'code'>({ source: 'code', identifiers: [code] })
}

export const deleteOrderBySystemId = ({ systemId, confirmed }: { systemId: number; confirmed: boolean }) => {
  return {
    type: DELETE_LINE_ITEMS_BY_SYSTEM_ID,
    payload: { systemId, confirmed },
  }
}

export const deleteLineItems = <T extends LineItemIdentifiableKeys>({
  source,
  identifiers,
}: {
  source: T
  identifiers: LineItemType[T][]
}) => ({
  type: DELETE_LINE_ITEMS,
  payload: { source, identifiers },
})

export const confirmLineItems = ({ uuids, confirmed }: { uuids: string[]; confirmed: boolean }) => ({
  type: CONFIRM_LINE_ITEMS,
  payload: { uuids, confirmed },
})

export const updateOrderQuantity = ({ uuid, quantity }: { uuid: string; quantity: number }) => {
  return updateLineItem<'quantity'>({
    uuid,
    source: 'quantity',
    value: quantity,
  })
}

export const updateOrderQuantityByCode = ({ code, quantity }: { code: string; quantity: number }) => {
  return updateLineItemByCode<'quantity'>({
    code,
    source: 'quantity',
    value: quantity,
  })
}

export const updateOrderBulkOption = ({ uuid, bulkOption }: { uuid: string; bulkOption: BulkOptionType | null }) => {
  return updateLineItem<'bulkOption'>({
    uuid,
    source: 'bulkOption',
    value: bulkOption,
  })
}

export const updateLineItem = <T extends keyof LineItemType>({
  uuid,
  source,
  value,
}: {
  uuid: string
  source: T
  value: LineItemType[T]
}): UpdateLineItemAction<T> => ({
  type: UPDATE_LINE_ITEM,
  payload: {
    uuid,
    source,
    value,
  },
})

export const updateLineItemByCode = <T extends keyof LineItemType>({
  code,
  source,
  value,
}: {
  code: string
  source: T
  value: LineItemType[T]
}): UpdateLineItemActionByCode<T> => ({
  type: UPDATE_LINE_ITEM_BY_CODE,
  payload: {
    code,
    source,
    value,
  },
})

export const updateLoadedProductData = (loadedData: LineItemLoadedDataType[], requestLineItems: LineItemType[]) => ({
  type: UPDATE_LOADED_PRODUCT_DATA,
  payload: { loadedData, requestLineItems },
})

export const updateSelectedHardwareSupplier = (
  selectedHardwareSupplier: HardwareSupplierFilterKeyType | undefined
) => ({
  type: UPDATE_SELECTED_HARDWARE_SUPPLIER,
  payload: { selectedHardwareSupplier },
})

export const updateIsCustomPricing = (usingCustomPricing: boolean) => ({
  type: UPDATE_IS_CUSTOM_PRICING,
  payload: usingCustomPricing,
})

export const updateLineItemSupplier = ({
  data,
  lineItemUuid,
  supplier,
}: {
  data: LineItemLoadedDataType
  lineItemUuid: string
  supplier: HardwareSupplierFilterKeyType
}) => ({
  type: UPDATE_LINE_ITEM_SUPPLIER,
  payload: { data, lineItemUuid, supplier },
})

export default function reducer(previousState: OrderComponentsType = getInitialState(), { type, payload }) {
  if (type === OPEN_ORDER_CART) {
    return {
      ...previousState,
      openDialog: payload,
    }
  }
  if (type === UPDATE_IS_CUSTOM_PRICING) {
    return {
      ...previousState,
      usingCustomPricing: payload,
    }
  }
  if (type === ADD_LINE_ITEMS) {
    const lineItemsToAdd = payload.lineItems
    const currentLineItems = previousState.lineItems
    const newLineItems = ProjectOrderPresenter.aggregateLineItems([...currentLineItems, ...lineItemsToAdd])
    persistCart(newLineItems)

    return {
      ...previousState,
      lineItems: newLineItems,
    }
  }

  if (type === DELETE_LINE_ITEMS_BY_SYSTEM_ID) {
    const { systemId, confirmed } = payload
    const currentLineItems = previousState.lineItems
    const newLineItems: LineItemType[] = []
    // When deleting by system ID (e.g. removing a project from MPO), check projectOrder to remove
    // only the allocation for this system and adjust quantity; drop the line item if no allocations remain.
    currentLineItems.forEach((lineItem: LineItemType) => {
      if (lineItem.confirmed !== confirmed) {
        newLineItems.push(lineItem)
      } else if (!lineItem.projectOrder) {
        newLineItems.push(lineItem)
      } else {
        let newQuantity = lineItem.quantity

        const newProjectOrder = lineItem.projectOrder.filter((detail: ProjectOrderDetail) => {
          if (detail.systemId !== systemId) {
            return true
          } else {
            // Exclude-from-order / manual cost items often have quantityPerItem undefined; treat as 1 so the line
            // is removed when fully allocated to this system (avoids duplicate rows after Update Cart).
            const qtyPerItem = lineItem.quantityPerItem ?? 1
            const deleteProjectRequiredQuantity = Math.ceil(detail.requiredQuantity / qtyPerItem)
            newQuantity -= deleteProjectRequiredQuantity
            return false
          }
        })

        // delete lineItem if there is no associate projects and unallocated stock
        if (newProjectOrder.length === 0 && newQuantity <= 0) return
        newLineItems.push(
          new OrderLineItem({
            ...lineItem,
            projectOrder: newProjectOrder,
            quantity: newQuantity,
          })
        )
      }
    })
    persistCart(newLineItems)
    return {
      ...previousState,
      lineItems: newLineItems,
    }
  }

  if (type === DELETE_LINE_ITEMS) {
    const { source, identifiers } = payload
    const currentLineItems = previousState.lineItems
    const newLineItems: LineItemType[] = []
    currentLineItems.forEach((lineItem: LineItemType) => {
      // can this trigger re-render??
      if (!identifiers.includes(lineItem[source])) {
        newLineItems.push(lineItem)
      }
    })
    persistCart(newLineItems)
    return {
      ...previousState,
      lineItems: newLineItems,
    }
  }

  if (type === CLEAR_LINE_ITEMS) {
    clearPersistedCart()
    return {
      ...previousState,
      lineItems: [],
    }
  }

  if (type === UPDATE_LINE_ITEM) {
    const { uuid, source, value } = payload
    const currentLineItems = previousState.lineItems
    const linItemToUpdate: LineItemType | undefined = currentLineItems.find(
      (lineItem: LineItemType) => lineItem.uuid === uuid
    )
    if (linItemToUpdate) {
      linItemToUpdate[source] = value
      // Sync pricePerUnit and selectedDistributorOrderingData when selectedDistributor changes
      // so cart and checkout show the same price (fixes cart vs checkout price mismatch).
      if (
        source === 'selectedDistributor' &&
        value &&
        Array.isArray(linItemToUpdate.orderingData) &&
        linItemToUpdate.orderingData.length > 0
      ) {
        const match = linItemToUpdate.orderingData.find((d) => d.distributor === value)
        if (match) {
          linItemToUpdate.pricePerUnit = match.price_detail?.price_ex_vat ?? match.price
          linItemToUpdate.selectedDistributorOrderingData = match
        }
      }
      // Keep projectOrder.requiredQuantity in sync when user edits quantity on cart (BOM flow)
      if (source === 'quantity' && linItemToUpdate.projectOrder?.length === 1) {
        linItemToUpdate.projectOrder[0].requiredQuantity = value as number
      }
    }

    const updatedLineItems = ProjectOrderPresenter.aggregateLineItems([...currentLineItems])
    persistCart(updatedLineItems)

    return {
      ...previousState,
      lineItems: updatedLineItems,
    }
  }

  if (type === CONFIRM_LINE_ITEMS) {
    const { uuids, confirmed } = payload
    const currentLineItems = previousState.lineItems
    uuids.forEach((uuid: string) => {
      const linItemToUpdate: LineItemType | undefined = currentLineItems.find(
        (lineItem: LineItemType) => lineItem.uuid === uuid
      )
      if (linItemToUpdate) {
        linItemToUpdate['confirmed'] = confirmed
      }
    })
    persistCart(currentLineItems)
    return {
      ...previousState,
      lineItems: [...currentLineItems],
    }
  }

  if (type === UPDATE_LOADED_PRODUCT_DATA) {
    const { loadedData, requestLineItems } = payload
    const lineItems = previousState.lineItems
    lineItems.forEach((lineItem: LineItemType, index: number) => {
      const requestLineItem: LineItemLoadedDataType = requestLineItems.find(
        (requestItem: LineItemType) => requestItem.code === lineItem.code
      )

      const matchedData: LineItemLoadedDataType = loadedData.find(
        (data: LineItemLoadedDataType) => data.code === lineItem.code
      )
      if (requestLineItem == null) {
        return
      }

      lineItems[index] = ProjectOrderPresenter.getLineItemByLoadedData(lineItem, matchedData)
    })
    const newLineItems = ProjectOrderPresenter.aggregateLineItems(lineItems)

    return {
      ...previousState,
      lineItems: newLineItems,
    }
  }

  if (type === UPDATE_SELECTED_HARDWARE_SUPPLIER) {
    const filterKey = payload.selectedHardwareSupplier

    if (filterKey) {
      appStorage.setString('selected_hardware_supplier', filterKey)
    } else {
      appStorage.clear('selected_hardware_supplier')
    }
    clearPersistedCart()
    return {
      ...previousState,
      selectedHardwareSupplier: payload.selectedHardwareSupplier,
    }
  }

  if (type === UPDATE_LINE_ITEM_SUPPLIER) {
    const { data, lineItemUuid, supplier } = payload
    const currentLineItems = previousState.lineItems
    const lineItemToUpdate: LineItemType | undefined = currentLineItems.find(
      (lineItem: LineItemType) => lineItem.uuid === lineItemUuid
    )

    if (lineItemToUpdate) {
      lineItemToUpdate['selectedDistributor'] = supplier
      lineItemToUpdate['stockLevels'] = data?.stockLevels || []
      lineItemToUpdate['data'] = {
        ...data,
        description: data?.description,
        short_description: data?.short_description || '',
        distributors: data ? [data] : [],
        manufacturer_name: data?.manufacturer,
        title: data?.title || '',
        image_url: data?.image_url,
      }
      lineItemToUpdate['selectedDistributorOrderingData'] = {
        ...data,
        description: data?.description,
        short_description: data?.short_description || '',
        distributors: data ? [data] : [],
        manufacturer_name: data?.manufacturer,
        title: data?.title || '',
        image_url: data?.image_url,
      }
    }
    const updatedLineItems = ProjectOrderPresenter.aggregateLineItems([...currentLineItems])
    persistCart(updatedLineItems)
    return {
      ...previousState,
      lineItems: updatedLineItems,
    }
  }

  if (type === UPDATE_LINE_ITEM_BY_CODE) {
    const { code, source, value } = payload
    const currentLineItems = previousState.lineItems
    const linItemToUpdate: LineItemType | undefined = currentLineItems.find(
      (lineItem: LineItemType) => lineItem.code === code
    )
    if (linItemToUpdate) {
      linItemToUpdate[source] = value
    }

    const updatedLineItems = ProjectOrderPresenter.aggregateLineItems([...currentLineItems])
    persistCart(updatedLineItems)
    return {
      ...previousState,
      lineItems: updatedLineItems,
    }
  }

  if (type === SET_CART_DEALS) {
    return {
      ...previousState,
      cartDeals: payload as Deal[],
    }
  }

  if (type === SET_ORG_ID) {
    return {
      ...previousState,
      cartDeals: undefined,
    }
  }

  return previousState
}

export function* injectPriceAndStock({ payload }: ReduxActionType) {
  const orgId = yield select(authSelectors.getOrgId)
  // Abort if data already loaded
  // TODO: refactor the distributor data as resource data
  if (payload.lineItems.every((lineItem: LineItemType) => lineItem.status === 'loaded')) {
    return
  }
  const selectedSupplier = yield select(orgSelectors.getSelectedHardwareSupplier)
  const supplierFilterKey = getSupplierConfigNonReact(selectedSupplier)?.filterKey
  const res: LineItemLoadedDataType[] = yield call(ProjectOrderPresenter.getPriceAndStock, {
    orgId,
    lineItems: payload.lineItems,
    supplierFilterKey,
  })
  if (res) {
    yield put({ type: UPDATE_LOADED_PRODUCT_DATA, payload: { loadedData: res, requestLineItems: payload.lineItems } })
  }
}

export function* orderLineItemSagas() {
  yield all([takeEvery(ADD_LINE_ITEMS, injectPriceAndStock)])
}

export const getProjectListByLineItems = (lineItems: LineItemType[]): number[] => {
  const projectList = new Set<number>()
  lineItems.forEach((lineItem: LineItemType) => {
    const projectOrder = lineItem.projectOrder || []
    projectOrder.forEach((order: ProjectOrderDetail) => {
      projectList.add(order.projectId)
    })
  })
  return Array.from(projectList)
}

export const orderSelectors = {
  getOrderLineItems: (state: RootState): LineItemType[] => {
    return state.orderComponents.lineItems
  },

  getConfirmedLineItems: createCachedReduxSelector((state: RootState): LineItemType[] => {
    return state.orderComponents.lineItems.filter((item) => item.confirmed)
  }),

  getUnconfirmedLineItems: createCachedReduxSelector((state: RootState): LineItemType[] => {
    return state.orderComponents.lineItems.filter((item) => !item.confirmed)
  }),

  getIsEmptyOrder: (state: RootState): boolean => {
    return state.orderComponents.lineItems.length === 0
  },

  getOrderProjectList: createCachedReduxSelector((state: RootState): number[] => {
    return getProjectListByLineItems(state.orderComponents.lineItems)
  }),

  getOrderProjectIds: createCachedReduxSelector((state: RootState): number[] => {
    const projectIds: number[] = []
    state.orderComponents.lineItems.forEach((lineItem: LineItemType) => {
      const projectOrderIds = lineItem.projectOrder?.map((detail) => detail.projectId) || []
      projectIds.push(...projectOrderIds)
    })
    return Array.from(new Set(projectIds))
  }),

  getUnconfirmedOrderProjectIds: createCachedReduxSelector((state: RootState): number[] => {
    const projectIds: number[] = []
    state.orderComponents.lineItems.forEach((lineItem: LineItemType) => {
      if (lineItem.confirmed) {
        return
      }
      const projectOrderIds = lineItem.projectOrder?.map((detail) => detail.projectId) || []
      projectIds.push(...projectOrderIds)
    })
    return Array.from(new Set(projectIds))
  }),

  getConfirmedOrderProjectIds: createCachedReduxSelector((state: RootState): number[] => {
    const projectIds: number[] = []
    orderSelectors.getConfirmedLineItems(state).forEach((lineItem: LineItemType) => {
      const projectOrderIds = lineItem.projectOrder?.map((detail) => detail.projectId) || []
      projectIds.push(...projectOrderIds)
    })
    return Array.from(new Set(projectIds))
  }),

  getLineItemsBySystemId: createCachedReduxSelector((state: RootState, systemId: number): LineItemType[] => {
    return state.orderComponents.lineItems.filter((lineItem) => {
      return lineItem.projectOrder?.some((a) => a.systemId === systemId)
    })
  }),

  getCartLineItemsForProjectAndSystem: (
    state: RootState,
    projectId: number,
    systemId: number
  ): LineItemType[] => {
    return state.orderComponents.lineItems
      .filter((lineItem) => lineItem.confirmed)
      .filter((lineItem) =>
        lineItem.projectOrder?.some((d) => d.projectId === projectId && d.systemId === systemId)
      )
  },

  getLineItemsByCode: createCachedReduxSelector((state: RootState, code: string): LineItemType[] => {
    return state.orderComponents.lineItems.filter((lineItem) => {
      return lineItem.code === code
    })
  }),

  getOrderableLineItems: createCachedReduxSelector((state: RootState): LineItemType[] => {
    const orderableLineItems = state.orderComponents.lineItems.filter((lineItem: LineItemType) => {
      if (!lineItem.confirmed) {
        return false
      }
      if (lineItem.status === 'loading') {
        return false
      }

      return ProjectOrderPresenter.getStockLevelStatusFromLineItem(lineItem) !== 'not_available'
    })
    return orderableLineItems
  }),

  getIsCustomPricing: (state: RootState): boolean => state.orderComponents.usingCustomPricing,

  getCartDeals: (state: RootState): Deal[] | undefined => state.orderComponents.cartDeals,
}
